import type { MouseEvent } from 'react';
import { useId } from 'react';
import { Box } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { useOpenSubscribe } from '@/features/subscribe/subscribeOfferContext';
import { useMe } from './meHooks';

export interface SubscriberAction {
  /** Runs `action` for a subscriber. Every other tier opens the purchase experience. */
  run: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Set when the click opens purchase, so the control is not a silent dead end. */
  description: string | undefined;
  descriptionId: string | undefined;
}

/**
 * The one subscriber-only create helper (TMMAPP-0090). Sites render the real control and call
 * `run`; they do not branch on the tier. A GUEST, NONE, or lapsed tier opens the subscribe dialog
 * (TMMAPP-0116: `offer` mode for a guest, `dismissible` in grace) over the page and
 * never creates. A guest keeps every shared virtual account.
 *
 * `managed`: the action is a Manager-eligible write the served standing already opens to this person
 * (TMMAPP-0110, MM-D-115 — a guest-seat Manager adds an envelope or a meeting); it runs at any tier.
 *
 * `purchase`: a site inside a dialog shows its own in-place subscribe step instead of stacking the
 * app-wide dialog (DLG-005, TMMAPP-0122 item 2).
 */
export function useSubscriberAction(
  action: (event: MouseEvent<HTMLButtonElement>) => void,
  managed = false,
  purchase?: () => void,
): SubscriberAction {
  const me = useMe();
  const openSubscribe = useOpenSubscribe();
  const { t } = useTranslation();
  const id = useId();
  const subscriber = managed || me.tier === 'SUBSCRIBER';

  return {
    run: (event) => {
      if (subscriber) {
        action(event);
        return;
      }
      if (purchase !== undefined) purchase();
      else openSubscribe();
    },
    description: subscriber ? undefined : t('subscribe.requiresSubscription'),
    descriptionId: subscriber ? undefined : id,
  };
}

export function SubscriberActionHint({
  id,
  description,
}: {
  id: string | undefined;
  description: string | undefined;
}) {
  if (description === undefined || id === undefined) return null;
  return (
    <Box component="span" id={id} sx={visuallyHidden}>
      {description}
    </Box>
  );
}
